import { Eye, EyeOff, LockIcon } from 'lucide-react';
import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import { useState } from 'react';
import { Control, FieldErrors, UseFormClearErrors, UseFormRegister, useWatch } from 'react-hook-form';

import { INPUT_APPEARANCE } from '@/components/ui/types/input-variants-enums';
import { Input } from '@/components/ui/input';
import { passwordRequirements } from '@/features/signup/helpers/password-requirements';
import { PasswordStrengthChecklist } from '@/features/signup/components/password-strength-checklist';
import type { RegistrationFormData } from '@/features/signup/types/use-registration-form-types';

const EyeIcon = <Eye className="h-4 w-4" aria-hidden="true" />;
const EyeOffIcon = <EyeOff className="h-4 w-4" aria-hidden="true" />;
const getPasswordIcon = (isVisible: boolean) => (isVisible ? EyeIcon : EyeOffIcon);

interface PasswordFieldGroupProps {
  control: Control<RegistrationFormData>;
  register: UseFormRegister<RegistrationFormData>;
  errors: FieldErrors<RegistrationFormData>;
  clearErrors: UseFormClearErrors<RegistrationFormData>;
  hideChecklist?: boolean;
}

export function PasswordFieldGroup({
  control,
  register,
  errors,
  clearErrors,
  hideChecklist,
}: PasswordFieldGroupProps) {
  const [showPassword, setShowPassword] = useState(false);

  const password = useWatch({ control, name: 'password' }) as string;

  const toggleVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const passwordReg = register('password', {
    required: 'Password is required',
    validate: value => {
      const isPasswordValid = passwordRequirements.every(req => req.test(value));
      if (!isPasswordValid) {
        return 'Password does not meet the minimum requirements.';
      }
      return true;
    },
  });

  return (
    <>
      <div className="space-y-1">
        <Input
          appearance={INPUT_APPEARANCE.DEFAULT}
          size={CONTROL_SIZE.COMFORTABLE}
          id="password"
          placeholder="Password"
          aria-label="Password"
          aria-invalid={errors.password ? true : undefined}
          aria-describedby={errors.password ? 'password-error' : undefined}
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          leftIcon={<LockIcon className="h-4 w-4" aria-hidden="true" />}
          rightIcon={getPasswordIcon(showPassword)}
          rightOnClick={toggleVisibility}
          rightAriaLabel={showPassword ? 'Hide password' : 'Show password'}
          rightAriaPressed={showPassword}
          onKeyDown={e => {
            if (e.key !== 'Enter') {
              clearErrors('password');
            }
          }}
          {...passwordReg}
        />
        {errors.password ? (
          <span
            id="password-error"
            className="type-field-description text-status-danger-foreground"
            role="alert"
          >
            {errors.password.message as string}
          </span>
        ) : null}
      </div>

      {hideChecklist ? null : <PasswordStrengthChecklist password={password ?? ''} />}
    </>
  );
}
